/**
 * DashboardComponent — top-level shell for the dashboard page.
 */

import React, { useState, useEffect } from "react";
import { Alert, Box, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { dashboardRegistry } from "./dashboardRegistry";
import DashboardSelector from "./DashboardSelector";
import DashboardShell from "./DashboardShell";

interface DashboardComponentProps {
  isOpen: boolean;
  menuWidth: number;
  darkMode?: boolean;
  onToggleDark?: () => void;
}

const DashboardComponent: React.FC<DashboardComponentProps> = ({
  isOpen,
  menuWidth,
  darkMode,
  onToggleDark,
}) => {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState<string>(
    dashboardRegistry.length > 0 ? dashboardRegistry[0].id : ""
  );

  useEffect(() => {
    if (
      dashboardRegistry.length > 0 &&
      !dashboardRegistry.find((d) => d.id === selectedId)
    ) {
      setSelectedId(dashboardRegistry[0].id);
    }
  }, [selectedId]);

  const selectedPlugin = dashboardRegistry.find((d) => d.id === selectedId);

  if (!isOpen) return null;

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        ml: `${menuWidth}px`,
        transition: theme.transitions.create("margin-left", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
        height: "100vh",
        overflow: "auto",
        bgcolor: theme.palette.mode === "dark" ? "grey.900" : "#f4f6f8",
      }}
    >
      <Box sx={{ p: 3, pt: 2 }}>
        {dashboardRegistry.length === 0 && (
          <Alert severity="info" sx={{ maxWidth: 480 }}>
            No dashboards available.
          </Alert>
        )}

        {dashboardRegistry.length > 1 && (
          <DashboardSelector
            dashboards={dashboardRegistry}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        )}

        {selectedPlugin ? (
          <DashboardShell
            plugin={selectedPlugin}
            darkMode={darkMode}
            onToggleDark={onToggleDark}
          />
        ) : (
          dashboardRegistry.length > 0 && (
            <Box sx={{ py: 4 }}>
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ mb: 2, borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default DashboardComponent;
